/*
 * @Descripttion: TntdForm.Item进行性能优化
 * @Author: 郑泳健
 * @Date: 2023-02-23 12:47:28
 * @LastEditors: 郑泳健
 * @LastEditTime: 2023-04-06 17:45:35
 */

import React, { useState, memo, useEffect, useImperativeHandle } from 'react'
import { getNames, handleCheckItem, getInitValue } from '../utils'
import type { IFormItem, IFormItemData, TinitialValueType, IChildrenCompFun } from '../interface'

const ItemComp: React.FC<IFormItem> = React.forwardRef<IChildrenCompFun, IFormItem>(
  (
    {
      name,
      children,
      style,
      initialValue,
      rules,
      shouldUpdate,
      dependencies,
      form,
      updateRefs,
      initialValues,
      compsRefs,
    },
    ref
  ) => {
    const [value, setValue] = useState<TinitialValueType>(undefined)
    const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
    const [refresh, setRefresh] = useState<boolean>(false)

    useImperativeHandle(ref, () => ({
      onRefresh: () => {
        setRefresh(!refresh)
      },
    }))

    /** 根据shouldUpdate 和 dependencies收集修改某个字段后需要同步更新的字段 */
    useEffect(() => {
      const nameStr = getNames(name)?.join('.')
      if (shouldUpdate) {
        updateRefs.current.shouldUpdateList.push(nameStr)
      }

      if (Array.isArray(dependencies)) {
        const arr = dependencies.map((i) => getNames(i).join('.'))
        if (Array.isArray(arr)) {
          arr.forEach((i) => {
            if (Array.isArray(updateRefs.current.dependencies[i])) {
              updateRefs.current.dependencies[i] = [
                ...updateRefs.current.dependencies[i],
                getNames(name).join('.'),
              ]
            } else {
              updateRefs.current.dependencies[i] = [getNames(name).join('.')]
            }
          })
        }
      }
    }, [shouldUpdate, dependencies, name, updateRefs])

    const updateRender = () => {
      if (Array.isArray(updateRefs.current.shouldUpdateList)) {
        updateRefs.current.shouldUpdateList.forEach((i) => {
          compsRefs?.current?.[i]?.()
        })
      }
      const list = updateRefs.current.dependencies[getNames(name).join('.')]

      if (Array.isArray(list)) {
        list.forEach((i) => {
          compsRefs?.current?.[i]?.()
        })
      }
    }

    /**
     * 动态修改form内部的值,为啥不在useEffect里面处理的原因是子组件的useEffect会比父组件useEffect先执行，需要保证在子组件useEffect监听value变化的时候获取到的formData永远是最新的
     * @param name
     * @param value
     * @returns
     */
    const generateFormData = (name: string[] | string, value: TinitialValueType) => {
      if (!name || !form || typeof name !== 'object') return
      const [str, ...rest] = name

      rest.forEach((i) => {
        if (form.formData[str]) {
          form.formData[str][i].value = value
          form.formData[str][i].checkItem = () => {
            const error = handleCheckItem(value, rules)
            setErrorMsg(error)
            return error
          }
        } else {
          form.formData[str] = {
            [i]: {
              value,
              checkItem: () => {
                const error = handleCheckItem(value, rules)
                setErrorMsg(error)
                return error
              },
              setItemValue: () => {},
              errorMsg: undefined,
            },
          }
        }
      })
    }

    // 为了给最外层提供方法去校验，比如保存的时候.
    // 这里依赖不要添加value,因为子组件的useEffect会比父组件useEffect先执行，如果添加了依赖value会导致子组件获取到的formData永远是上一次的
    useEffect(() => {
      if (!name || !form || typeof name !== 'object') return
      const [str, ...rest] = name

      const addItem: IFormItemData = {
        setItemValue: (_val, callback) => {
          const error = handleCheckItem(_val, rules)
          setValue(_val)
          setErrorMsg(error)
          callback?.()
          updateRender()
        },
        checkItem: () => {
          const error = handleCheckItem(value, rules)
          setErrorMsg(error)
          return error
        },
        value,
        errorMsg,
      }
      rest.forEach((i) => {
        if (form.formData[str]) {
          form.formData[str][i] = addItem
        } else {
          form.formData[str] = {
            [i]: addItem,
          }
        }
      })
    }, [rules, name, errorMsg, form])

    /** 这段代码不能删除，当增加/删除列的时候有些默认值需要更新 */
    useEffect(() => {
      if (initialValue) {
        setValue(initialValue)
        generateFormData(name, initialValue)
        updateRender()
      }
    }, [initialValue])

    // 初始化设置默认值,
    useEffect(() => {
      const initValue = getInitValue(initialValues, name)
      // @ts-ignore
      const _value = ['', null, undefined].includes(initValue) ? initialValue : initValue

      setValue(_value)
      generateFormData(name, _value)
      updateRender()
    }, [])

    // 默认方法
    const handleChange = (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
        | string
        | number
        | undefined
    ) => {
      let val
      if (typeof e === 'object') {
        val = (e as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>)
          ?.target?.value
      } else {
        val = e
      }
      setValue(val)
      generateFormData(name, val)
      const error = handleCheckItem(val, rules)
      setErrorMsg(error)
      updateRender()
    }

    return (
      <div
        className={errorMsg ? 'has-error custom-form-item-wrapper' : 'custom-form-item-wrapper'}
        style={style}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              value,
              form,
              onChange: handleChange,
              'data-key': getNames(name)?.join('.'),
              ...child.props,
            })
          }
          return null
        })}
        {errorMsg && <div className="ant-form-explain ant-form-item-explain-error">{errorMsg}</div>}
      </div>
    )
  }
)

function arePropsEqual(prevProps: IFormItem, nextProps: IFormItem) {
  if (!nextProps.shouldUpdate && Array.isArray(nextProps.dependencies)) {
    return true
  }

  return false
}

export default memo(ItemComp)
